import { DateCell } from "@/components/shared/cells/date-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TypographyP } from "@/components/ui/typography";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, Eye, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const selectColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-full w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const fileNameColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "fileName",
  accessorKey: "invoice.fileName",
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title="Dateiname" />;
  },
  cell: (cell) => {
    return (
      <TypographyP className="truncate">
        {cell.row.original.invoice.fileName}
      </TypographyP>
    );
  },
};

const createdAtColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "createdAt",
  size: 130,
  accessorKey: "invoice.createdAt",
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("created_at")} />
    );
  },
  cell: (cell) => {
    return <DateCell value={cell.row.original.invoice.createdAt} />;
  },
};

function actionsColumn({
  onView,
  onDownload,
  onDelete,
}: {
  onView: (invoiceId: string) => void;
  onDownload: (invoiceId: string, invoiceFileName: string) => void;
  onDelete: (invoiceId: string) => void;
}): ColumnDef<GetAllInvoiceInvoice> {
  return {
    id: "actions",
    size: 150,
    header: (header) => {
      return (
        <DataTableColumnHeader
          column={header.column}
          title={"Aktionen"}
          className="text-end px-4"
        />
      );
    },
    cell: (cell) => {
      const { t } = useTranslation();

      return (
        <div className="flex items-center justify-end h-full w-full gap-1 px-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-gray-200"
                  onClick={() => {
                    onView(cell.row.original.invoiceDetailsId);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("transactions.actions.open")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-gray-200"
                  onClick={() => {
                    onDownload(
                      cell.row.original.invoiceDetailsId,
                      cell.row.original.invoice.fileName,
                    );
                  }}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("download")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-gray-200"
                  onClick={() => {
                    onDelete(cell.row.original.invoiceDetailsId);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("delete")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  };
}

export function buildColumns({
  onView,
  onDownload,
  onDelete,
}: {
  onView: (invoiceId: string) => void;
  onDownload: (invoiceId: string, invoiceFileName: string) => void;
  onDelete: (invoiceId: string) => void;
}) {
  return [
    selectColumn,
    fileNameColumn,
    createdAtColumn,
    actionsColumn({ onView, onDownload, onDelete }),
  ];
}
